// FormField.tsx
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  VStack,
  Box,
  FormControl,
  Input,
  InputField,
  RadioGroup,
  Radio,
  RadioIndicator,
  RadioIcon,
  CircleIcon,
  RadioLabel,
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckIcon,
  CheckboxLabel,
  FormControlLabel,
  FormControlLabelText,
  FormControlError,
  FormControlErrorText,
  Textarea,
  TextareaInput,
  HStack,
  Select,
  SelectTrigger,
  SelectInput,
  SelectIcon,
  SelectPortal,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicatorWrapper,
  SelectDragIndicator,
  SelectItem,
  ChevronDownIcon,
  FormControlHelperText,
  FormControlHelper,
  Switch,
} from '@gluestack-ui/themed';
import Quill from '../Quill';
import { shouldCommitQuillHtml } from './quillHtmlChange';
import FieldLabelAction from './FieldLabelAction';

/** Native HTML date input styles — sits inside Gluestack Input chrome on web. */
const WEB_DATE_INPUT_STYLE: React.CSSProperties = {
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  width: '100%',
  height: '100%',
  fontSize: 12,
  paddingLeft: 8,
  paddingRight: 8,
  boxSizing: 'border-box',
};

interface Choice {
  label: string;
  value: string | number | boolean;
  isDisabled?: boolean;
}

type AutoCompleteValue = React.ComponentProps<typeof InputField>['autoComplete'];

interface FormFieldProps {
  name: string;
  label?: string;
  type: 'text' | 'textarea' | 'radio' | 'select' | 'rte' | 'checkbox' | 'switch' | 'number' | 'password' | 'date';
  readonly?: boolean;
  placeholder?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules?: any;
  choices?: Choice[];
  helperText?: string;
  horizontal?: boolean;
  labelSize?: 'xs' | 'sm' | 'md' | 'lg';
  errorTextSize?: '2xs' | 'xs' | 'sm';
  fieldWidth?: string | number;
  fieldHeight?: string | number;
  /**
   * RTE-only: when both fieldMinHeight and fieldMaxHeight are set, the editor
   * grows from min to max with its content (no fixed height). Above max the
   * editor scrolls internally with hidden scrollbar chrome.
   */
  fieldMinHeight?: number;
  fieldMaxHeight?: number;
  fieldSize?: 'xs' | 'sm' | 'md' | 'lg';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldStyle?: any;
  labelAction?: {
    enabled: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
    isLoading?: boolean;
    action?: () => void;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldRef?: React.Ref<any>;
  borderless?: boolean;
  hideToolbar?: boolean;
  isRequired?: boolean;
  autoComplete?: AutoCompleteValue;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (val: any) => void;
}

const FieldLabel: React.FC<{
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  labelSize?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  labelAction?: any
}> = ({
  label,
  labelSize,
  labelAction,
}) => (
  <FormControlLabel>
    <HStack alignItems="center">
      {label ? <FormControlLabelText size={labelSize}>{label}</FormControlLabelText> : null}
      <FieldLabelAction labelAction={labelAction} />
    </HStack>
  </FormControlLabel>
);

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type,
  readonly = false,
  placeholder,
  defaultValue,
  rules,
  choices,
  helperText,
  horizontal,
  labelSize = 'xs',
  errorTextSize = '2xs',
  fieldWidth,
  fieldHeight = 40,
  fieldMinHeight,
  fieldMaxHeight,
  fieldSize = 'xs',
  fieldStyle,
  labelAction,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fieldRef,
  borderless = false, // For RTE fields - removes borders for seamless integration
  hideToolbar = false, // For RTE fields - hides formatting toolbar
  isRequired = false,
  autoComplete,
}) => {
  if (readonly && labelAction) {
    // eslint-disable-next-line no-param-reassign
    labelAction.enabled = false;
  }

  const {
    formState: { errors },
  } = useFormContext();

  const renderField = () => {
    switch (type) {
      case 'text':
      case 'number':
      case 'password':
        return (
          <Box
            justifyContent={horizontal ? 'space-between' : 'flex-start'}
            flexDirection={horizontal ? 'row' : 'column'}
          >
            <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Input isReadOnly={readonly} isDisabled={readonly} w={fieldWidth as any} h={fieldHeight as any} size={fieldSize as any}>
              <Controller
                name={name}
                defaultValue={defaultValue || ''}
                rules={rules}
                render={({ field: { onChange, ...fieldProps } }) => (
                  <InputField
                    testID={`form-field-input-${name}`}
                    aria-label={`Inputfield ${label}`}
                    placeholder={placeholder}
                    onChangeText={onChange}
                    type={type === 'password' ? 'password' : 'text'}
                    keyboardType={type === 'number' ? 'numeric' : 'default'}
                    autoComplete={autoComplete}
                    {...fieldProps}
                  />
                )}
              />
            </Input>
          </Box>
        );

      case 'date':
        // RN Web's TextInput ignores type="date" (only email/tel/search/url/text/password).
        // On web, render a real HTML date input inside Gluestack Input chrome.
        return (
          <Box
            justifyContent={horizontal ? 'space-between' : 'flex-start'}
            flexDirection={horizontal ? 'row' : 'column'}
          >
            <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Input isReadOnly={readonly} isDisabled={readonly} w={fieldWidth as any} h={fieldHeight as any} size={fieldSize as any}>
              <Controller
                name={name}
                defaultValue={defaultValue || ''}
                rules={rules}
                render={({ field: { onChange, onBlur, value } }) => (
                  // Always use a real HTML date input. RN Web's TextInput never
                  // forwards type="date"; lecturer calendar is web-first.
                  React.createElement('input', {
                    type: 'date',
                    value: value || '',
                    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
                    onBlur,
                    disabled: readonly,
                    readOnly: readonly,
                    'aria-label': `Inputfield ${label}`,
                    testID: `form-field-input-${name}`,
                    'data-testid': `form-field-input-${name}`,
                    style: WEB_DATE_INPUT_STYLE,
                  })
                )}
              />
            </Input>
          </Box>
        );

      case 'switch':
        return (
          <Box
            justifyContent={horizontal ? 'space-between' : 'flex-start'}
            flexDirection={horizontal ? 'row' : 'column'}
          >
            <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />
            <Controller
              name={name}
              defaultValue={defaultValue || false}
              rules={rules}
              render={({ field: { onChange, value } }) => (
                <Switch
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  size={fieldSize as any}
                  isDisabled={readonly}
                  onToggle={(val) => onChange(val)}
                  value={value || false}
                />
              )}
            />
          </Box>
        );

      case 'textarea':
        return (
          <Box
            justifyContent={horizontal ? 'space-between' : 'flex-start'}
            flexDirection={horizontal ? 'row' : 'column'}
          >
            <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />
            <Textarea
              isReadOnly={readonly}
              isDisabled={readonly}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              w={fieldWidth as any}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              h={fieldHeight as any || 'auto'}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              size={fieldSize as any}
            >
              <Controller
                name={name}
                defaultValue={defaultValue || ''}
                rules={rules}
                render={({ field: { onChange, ...fieldProps } }) => (
                  <TextareaInput
                    testID={`form-field-input-${name}`}
                    aria-label={`Inputfield ${label}`}
                    placeholder={placeholder}
                    onChangeText={onChange}
                    {...fieldProps}
                  />
                )}
              />
            </Textarea>
          </Box>
        );

      case 'radio':
        return (
          <Box
            justifyContent={horizontal ? 'space-between' : 'flex-start'}
            flexDirection={horizontal ? 'row' : 'column'}
          >
            <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />
            <Controller
              name={name}
              defaultValue={defaultValue || null}
              rules={rules}
              render={({ field }) => (
                <RadioGroup {...field} isReadOnly={readonly} isDisabled={readonly}>
                  <VStack space="sm">
                    {choices?.map((option) => (
                      <Radio
                        aria-label={`Radiocontrol ${label}`}
                        {...field}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        size={fieldSize as any}
                        key={String(option.value)}
                        value={String(option.value)}
                      >
                        <RadioIndicator mr="$2">
                          <RadioIcon as={CircleIcon} />
                        </RadioIndicator>
                        <RadioLabel>{option.label}</RadioLabel>
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              )}
            />
          </Box>
        );

      case 'select':
        return (
          <Box justifyContent="space-between" flexDirection={horizontal ? 'row' : 'column'}>
            <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />
            <Controller
              name={name}
              defaultValue={defaultValue || null}
              rules={rules}
              render={({ field }) => (
                <Select
                  testID={`form-field-select-${name}`}
                  key={field.value} // this forces rerender when field.value changes
                  aria-label={`Select Dropdown ${label}`}
                  selectedValue={field.value}
                  initialLabel={choices?.filter((option) => option.value === field.value)[0]?.label || null}
                  onValueChange={field.onChange}
                  isDisabled={readonly || !choices || choices.length === 0}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  width={fieldWidth as any}
                >
                  <SelectTrigger
                    testID={`form-field-trigger-${name}`}
                    style={fieldStyle}
                    variant="outline"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    size={fieldSize as any || 'md'}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    height={fieldHeight as any || undefined}
                  >
                    <SelectInput
                      testID={`form-field-input-${name}`}
                      placeholder={(!choices || choices.length === 0) ? 'No options available' : (placeholder || 'Select option')}
                    />
                    <SelectIcon mr="$3" as={ChevronDownIcon} />
                  </SelectTrigger>

                  <SelectPortal>
                    <SelectBackdrop />
                    <SelectContent>
                      <SelectDragIndicatorWrapper>
                        <SelectDragIndicator />
                      </SelectDragIndicatorWrapper>

                      {/* Select Items */}
                      {choices?.map((option) => (
                        <SelectItem
                          testID={`form-field-option-${name}-${option.value}`}
                          key={String(option.value)}
                          label={option.label}
                          value={String(option.value)}
                          isDisabled={option.isDisabled}
                        />
                      ))}
                    </SelectContent>
                  </SelectPortal>
                </Select>
              )}
            />
          </Box>
        );

      case 'rte':
        return (
          <Box>
            {!!label && <FieldLabel label={label} labelSize={labelSize} labelAction={labelAction} />}
            <Controller
              name={name}
              defaultValue={defaultValue}
              rules={{
                validate: (value) => {
                  // handle undefined/null values
                  const cleanedValue = (value || '').replace(/<[^>]*>/g, '');
                  if (rules?.required && cleanedValue.length === 0) {
                    const isRequired = typeof rules.required === 'object' ? rules.required.value : !!rules.required;
                    if (isRequired) {
                      return typeof rules.required === 'object' ? rules.required.message : rules.required;
                    }
                  }
                  // apply minLength, maxLength if set
                  if (rules?.minLength && cleanedValue.length < rules.minLength.value) {
                    return rules.minLength?.message || `Min length is ${rules.minLength.value}`;
                  }
                  if (rules?.maxLength && cleanedValue.length > rules.maxLength.value) {
                    return rules.maxLength?.message || `Max length is ${rules.maxLength.value}`;
                  }
                  // apply custom validate functions if provided in rules (as an object of functions or a single function)
                  if (rules?.validate) {
                    if (typeof rules.validate === 'function') {
                      return rules.validate(value);
                    }
                    if (typeof rules.validate === 'object') {
                      const keys = Object.keys(rules.validate);
                      for (let i = 0; i < keys.length; i += 1) {
                        const key = keys[i];
                        const result = rules.validate[key](value);
                        if (result !== true) return result;
                      }
                    }
                  }
                  if (rules?.validation) {
                    return rules.validation(cleanedValue);
                  }
                  return true;
                },
              }}
              render={({
                field: {
                  onChange, value, name: fieldName,
                },
              }) => {
                // Grow mode: editor sizes to its content between min/max,
                // hiding native scrollbar chrome. Falls back to fixed height
                // when min/max aren't both provided (legacy callers).
                const useGrow = fieldMinHeight != null && fieldMaxHeight != null;
                const boxSizing = useGrow
                  ? { minHeight: fieldMinHeight, maxHeight: fieldMaxHeight }
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  : { h: (fieldHeight as any) || 200 };
                const quillSx: Record<string, unknown> = {
                  backgroundColor: borderless ? 'transparent' : 'white',
                };
                if (useGrow) {
                  // Hide native scroll chrome on web; the cropped last line
                  // remains as a soft "more below" affordance. `_web` is
                  // gluestack-ui's required pseudo-prop for web-only sx.
                  // eslint-disable-next-line no-underscore-dangle
                  quillSx._web = {
                    scrollbarWidth: 'none',
                    '::-webkit-scrollbar': { display: 'none' },
                  };
                }

                return (
                  <Box
                    testID={`form-field-rte-${name}`}
                    {...boxSizing}
                    flexGrow={1}
                    borderRadius={borderless ? 0 : 9}
                    overflow="hidden"
                    borderWidth={errors[fieldName] ? 1 : 0}
                    borderColor="$error700"
                  >
                    <Quill
                      sx={quillSx}
                      value={value || ''}
                      initialHtml={defaultValue}
                      compact
                      borderless={borderless}
                      hideToolbar={hideToolbar}
                      readonly={readonly}
                      onHtmlChange={(html, meta) => {
                        // Skip non-user sources and empty-HTML oscillations
                        // that re-enter RHF → controlled Quill (FLARE-7D).
                        if (shouldCommitQuillHtml(html, value, meta?.source)) {
                          onChange(html);
                        }
                      }}
                      placeholder={placeholder || ''}
                    />
                  </Box>
                );
              }}
            />
          </Box>
        );

      case 'checkbox':
        return (
          <Controller
            name={name}
            defaultValue={defaultValue || false}
            rules={rules}
            render={({ field }) => (
              <Checkbox
                aria-label={`Checkbox ${label}`}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                size={fieldSize as any}
                isChecked={field.value}
                onChange={field.onChange}
                isReadOnly={readonly}
                isDisabled={readonly}
                value={name}
              >
                <CheckboxIndicator mr="$2">
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <CheckboxLabel size={labelSize as any || 'md'}>{label}</CheckboxLabel>
              </Checkbox>
            )}
          />
        );

      default:
        return null;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getNestedError(errs: any, fieldName: string) {
    const fields = fieldName.split('.');
    let currentError = errs;

    for (const field of fields) {
      if (!currentError || currentError[field] === undefined) {
        return ''; // No error found for this field
      }
      currentError = currentError[field];
    }
    // Return the error message if it exists, otherwise an empty string
    return currentError || {};
  }

  return (
    <FormControl testID={`form-control-${name}`} isInvalid={!!getNestedError(errors, name)} isRequired={isRequired}>
      {renderField()}
      <FormControlError justifyContent={horizontal ? 'flex-end' : 'flex-start'}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <FormControlErrorText testID={`form-field-error-${name}`} size={errorTextSize as any || '2xs'}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {(getNestedError(errors, name) as any)?.message}
        </FormControlErrorText>
      </FormControlError>
      <FormControlHelper>
        {!!helperText && (
          // Keep helper subordinate to the label (labels default to xs).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <FormControlHelperText size={errorTextSize as any || '2xs'}>
            {helperText}
          </FormControlHelperText>
        )}
      </FormControlHelper>
    </FormControl>
  );
};

export default FormField;
